import React, { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import classNames from 'classnames';
import SegmentedControl from '../../SegmentedControl';
import RightPanel from '../Dashboard/RightPanel';
import InterviewCard from './InterviewCard';
import CandidateDetails from '../CandidateDetails';
import CalendarService from '../../../services/CalendarService';
import UserService from '../../../services/UserService';
import { getEvents } from '../../../helpers/getEvents';
import classes from './styles.module.scss';

export default function InterviewDashboard() {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState({ value: 'upcoming' });
  const [isCandidatePopupVisible, setIsCandidatePopupVisible] = useState(false);
  const [currentCandidate, setCurrentCandidate] = useState(null);
  const [currentEvent, setCurrentEvent] = useState(null);

  const { data: user } = useQuery({
    queryKey: ['me'],
    queryFn: UserService.getMe,
  });
  const { data: calendarEvents, refetch: refetchCalendarEvents } = useQuery({
    queryKey: ['getInteviewerEvents'],
    queryFn: CalendarService.getInteviewerEvents,
    keepPreviousData: true,
  });

  const allEvents = useMemo(() => {
    return getEvents(
      calendarEvents,
      user?.id,
      null,
      user?.userProfile?.timezone
    );
  }, [calendarEvents, user]);

  // Sync currentEvent with refetched data
  useEffect(() => {
    if (currentEvent && allEvents) {
      const updatedEvent = allEvents.find(
        (event) => event?.id === currentEvent?.id
      );
      if (updatedEvent) {
        setCurrentEvent(updatedEvent);
        setCurrentCandidate(updatedEvent.originalEvent.jobApplication);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEvents, currentEvent?.id]);

  const upcoming = useMemo(
    () =>
      allEvents
        ?.filter(
          (e) =>
            e.type === 'Interview' &&
            [
              'Scheduled',
              'Accepted',
              'New',
              'Rescheduled',
              'Pending',
              'Canceled',
              'Declined',
            ].includes(e.status) &&
            Date.parse(e.eventDate) >= new Date()
        )
        .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate)),
    [allEvents]
  );

  const completed = useMemo(
    () =>
      allEvents
        ?.filter(
          (e) =>
            e.type === 'Interview' &&
            Date.parse(e.eventDate) < new Date() &&
            ['Accepted', 'Completed'].includes(e.status)
        )
        .sort((a, b) => new Date(b.eventDate) - new Date(a.eventDate)),
    [allEvents]
  );

  const pendingFeedback = useMemo(
    () =>
      allEvents
        ?.filter(
          (e) =>
            e.type === 'Interview' &&
            Date.parse(e.eventDate) < new Date() &&
            ['Accepted', 'Completed'].includes(e.status) &&
            !e.event.jobApplicationInterviewFeedbacks.some(
              (feedback) => feedback.userId === user.id
            )
        )
        .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate)),
    [allEvents, user]
  );

  const segmentItems = useMemo(() => {
    return [
      {
        label: t('pages.InterviewDashboard.upcomingInterviews'),
        value: 'upcoming',
        count: upcoming?.length || 0,
      },
      {
        label: t('pages.InterviewDashboard.completedInterviews'),
        value: 'completed',
        count: completed?.length || 0,
      },
    ];
  }, [upcoming, completed, t]);

  const showList = selectedTab?.value === 'upcoming' ? upcoming : completed;

  return (
    <div className={classes.InterviewDashboard}>
      <div className={classes.leftCol}>
        <div className={classes.row}>
          <div className={classes.col}>
            <div className={classes.listCol}>
              <div className={classes.listHeader}>
                <SegmentedControl
                  ariaLabel={t('pages.InterviewDashboard.segmentAria')}
                  items={segmentItems}
                  selectedItem={selectedTab}
                  onChange={(item) => setSelectedTab(item)}
                />
              </div>
              <div
                className={classNames(classes.listScroll, {
                  [classes.empty]: !showList?.length,
                })}
              >
                {!showList?.length ? (
                  <div className={classes.emptyText}>
                    {selectedTab?.value === 'upcoming'
                      ? t('pages.InterviewDashboard.emptyText')
                      : t('pages.InterviewDashboard.emptyTextCompleted')}
                  </div>
                ) : (
                  <div className={classes.cards}>
                    {showList?.map((event) => (
                      <div
                        key={`${event.id}-${event.status}`}
                        className={classes.cardWrap}
                      >
                        <InterviewCard
                          event={event}
                          user={user}
                          refetch={refetchCalendarEvents}
                          onViewCandidate={() => {
                            setCurrentEvent(event);
                            setCurrentCandidate(
                              event.originalEvent.jobApplication
                            );
                            setIsCandidatePopupVisible(true);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={classes.col}>
            <div className={classes.listCol}>
              <div className={classes.listHeader}>
                <div className={classes.pendingHeaderRow}>
                  <h3>{t('pages.InterviewDashboard.pendingFeedback')}</h3>
                  <span
                    className={classNames(classes.countBadge, {
                      [classes.red]: pendingFeedback?.length > 0,
                      [classes.zero]: pendingFeedback?.length === 0,
                    })}
                  >
                    {pendingFeedback?.length || 0}
                  </span>
                </div>
              </div>
              <div
                className={classNames(classes.listScroll, {
                  [classes.empty]: !pendingFeedback?.length,
                })}
              >
                {!pendingFeedback?.length ? (
                  <div className={classes.emptyText}>
                    <span>
                      {' '}
                      {t('pages.InterviewDashboard.emptyTextFeedback')}
                    </span>
                  </div>
                ) : (
                  <div className={classes.cards}>
                    {pendingFeedback?.map((event) => (
                      <div key={`pf-${event.id}`} className={classes.cardWrap}>
                        <InterviewCard
                          event={event}
                          refetch={refetchCalendarEvents}
                          user={user}
                          onViewCandidate={() => {
                            setCurrentEvent(event);
                            setCurrentCandidate(
                              event.originalEvent.jobApplication
                            );
                            setIsCandidatePopupVisible(true);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={classes.rightCol}>
        <RightPanel isInterview />
      </div>

      <CandidateDetails
        isVisible={isCandidatePopupVisible}
        setIsVisible={() => {
          setIsCandidatePopupVisible();
          setCurrentEvent(null);
          setCurrentCandidate(null);
        }}
        event={currentEvent}
        refetch={refetchCalendarEvents}
        candidate={
          currentCandidate?.user || currentCandidate?.jobApplication?.user
        }
        jobApplicationId={
          currentCandidate?.id || currentCandidate?.jobApplication?.id
        }
        itemIds={[]}
        setCurrentItemId={() => {}}
      />
    </div>
  );
}
